import { useEffect, useRef } from "react";

/** Ordine sopra la soglia SDA (15 colli) in attesa di conferma switch. */
export type CarrierSwitchItem = {
  reference: string;
  packages_count: number;
};

interface CarrierSwitchConfirmDialogProps {
  /** ``single`` = un ordine (dalla card); ``bulk`` = lista cumulativa (toolbar). */
  mode: "single" | "bulk";
  items: CarrierSwitchItem[];
  onConfirm: () => void;
  onCancel: () => void;
}

const TITLE_ID = "carrier-switch-confirm-title";
const TARGET_CARRIER = "Poste Delivery Business";
const SDA_MAX_PACKAGES = 15;

/**
 * Popup di conferma switch corriere SDA → Poste Delivery Business quando i colli
 * effettivi superano il limite SDA (15). Stesso pattern modale di
 * ``PackagesConfirmDialog``/``CosmaConfirmDialog`` (role=dialog, overlay, Esc,
 * footer Annulla/Conferma). A differenza di quelli non ha editor né stato
 * ``sending``: l'esecuzione (e il relativo busy/feedback) la gestisce il parent,
 * che smonta il dialog alla scelta.
 *
 * - ``single``: messaggio puntuale "{N} colli supera il limite SDA (15)".
 * - ``bulk``: elenco cumulativo degli ordini sopra soglia.
 */
export function CarrierSwitchConfirmDialog({
  mode,
  items,
  onConfirm,
  onCancel,
}: CarrierSwitchConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    cancelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const count = items.length;
  const first = items[0];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-serif text-[17px] font-medium text-ink">
            Cambio corriere: oltre {SDA_MAX_PACKAGES} colli
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-3">
            {mode === "single"
              ? `SDA non accetta più di ${SDA_MAX_PACKAGES} colli per spedizione.`
              : "Alcuni ordini selezionati non sono spedibili con SDA."}
          </p>
        </header>

        <div className="max-h-[60vh] overflow-y-auto px-5 py-4 text-[12.5px] text-ink leading-relaxed">
          {mode === "single" && first ? (
            <>
              <p className="mb-1">
                Ordine <span className="font-mono font-medium">{first.reference}</span>
              </p>
              <p>
                {first.packages_count} colli supera il limite SDA ({SDA_MAX_PACKAGES}): genero con{" "}
                <strong>{TARGET_CARRIER}</strong>?
              </p>
            </>
          ) : (
            <>
              <p className="mb-2">
                {count} ordin{count === 1 ? "e" : "i"} super{count === 1 ? "a" : "ano"} i{" "}
                {SDA_MAX_PACKAGES} colli SDA e verr{count === 1 ? "à" : "anno"} spedit
                {count === 1 ? "o" : "i"} con <strong>{TARGET_CARRIER}</strong>:
              </p>
              <ul className="space-y-1">
                {items.map((item) => (
                  <li key={item.reference} className="flex items-baseline gap-2">
                    <span className="font-mono text-[12px] font-medium text-ink-2">
                      {item.reference}
                    </span>
                    <span className="text-[11.5px] tabular-nums text-ink-3">
                      {item.packages_count} colli
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <footer className="border-t border-line px-5 py-3">
          <div className="flex items-center justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              data-testid="carrier-switch-cancel-btn"
              onClick={onCancel}
              className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper"
            >
              Annulla
            </button>
            <button
              type="button"
              data-testid="carrier-switch-confirm-btn"
              onClick={onConfirm}
              className="rounded-sm border border-violet bg-violet px-[14px] py-2 text-[13px] font-medium text-white hover:bg-violet-2"
            >
              {mode === "single" ? "Conferma e genera" : `Conferma e genera (${count})`}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
